import { Typewriter } from "@/components/ui/typewriter";
import { motion, useReducedMotion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";

import metaIcon from "@/assets/meta-icon.webp";
import googleAds from "@/assets/google-ads.webp";
import linkedinIcon from "@/assets/linkedin-icon.webp";
import cursorIcon from "@/assets/cursor-icon.webp";

interface FloatingImageProps {
  src: string;
  alt: string;
  className: string;
  delay: number;
  size?: string;
  active: boolean;
}

function FloatingImage({ src, alt, className, delay, size = "w-20 h-20 md:w-28 md:h-28", active }: FloatingImageProps) {
  const reduced = useReducedMotion();
  const animate = reduced || !active
    ? { opacity: 1, y: 0 }
    : { y: [0, -18, 0], opacity: 1 };
  return (
    <motion.div
      className={`absolute pointer-events-none ${className}`}
      initial={reduced ? { opacity: 1 } : { y: 60, opacity: 0 }}
      animate={animate}
      transition={reduced || !active ? {} : {
        y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay },
        opacity: { duration: 0.8, delay: delay * 0.3 },
      }}
    >
      <img
        src={src}
        alt={alt}
        width={112}
        height={112}
        loading="eager"
        decoding="async"
        className={`${size} object-contain drop-shadow-xl`}
      />
    </motion.div>
  );
}

export function HeroSection() {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.1 });

  return (
    <div ref={ref} id="hero" className="relative w-full flex items-center justify-center overflow-hidden pt-24 pb-20 md:py-0 md:min-h-screen md:h-screen">
      <FloatingImage active={inView} src={linkedinIcon} alt="LinkedIn" className="hidden md:block top-[12%] left-[10%] rotate-[-12deg]" delay={0} />
      <FloatingImage active={inView} src={googleAds} alt="Google Ads" className="hidden md:block top-[8%] right-[12%] rotate-[8deg]" delay={0.5} />
      <FloatingImage active={inView} src={linkedinIcon} alt="LinkedIn" className="md:hidden top-20 left-2 rotate-[-12deg]" delay={0} size="w-14 h-14" />
      <FloatingImage active={inView} src={googleAds} alt="Google Ads" className="md:hidden top-20 right-2 rotate-[8deg]" delay={0.5} size="w-14 h-14" />
      <FloatingImage active={inView} src={metaIcon} alt="Meta" className="bottom-2 left-2 md:bottom-[18%] md:left-[14%] rotate-[6deg]" delay={1} size="w-14 h-14 md:w-28 md:h-28" />
      <FloatingImage active={inView} src={cursorIcon} alt="Click" className="bottom-2 right-2 md:bottom-[22%] md:right-[10%] rotate-[-6deg]" delay={1.5} size="w-14 h-14 md:w-28 md:h-28" />

      <div className="relative z-10 text-center px-4">
        <div
          aria-hidden="true"
          className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-foreground leading-tight tracking-tight"
        >
          <span className="font-bold">You need</span>
          <br />
          <span className="font-light">
            {inView ? (
              <Typewriter words={["More", "Qualified", "Consistent", "Predictable", "Retained"]} speed={80} delayBetweenWords={2000} cursorChar="|" />
            ) : (
              <span>More</span>
            )}
          </span>
          <br />
          <span className="font-bold">Clients</span>
        </div>
        <p className="mt-6 text-base md:text-xl text-muted-foreground">Your firm is ready. Your pipeline should be too</p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          className="mt-8 bg-primary text-primary-foreground font-semibold text-lg px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-shadow"
          onClick={() => navigate("/audit")}
        >
          Let's Talk
        </motion.button>
      </div>
    </div>
  );
}
